import { FC, ReactNode, useEffect, useMemo, useState } from 'react';
import {
	ExportButtons,
	HeaderContainer,
	PaginationContainer,
	ReportHeaderContainer,
	TableBody,
	TableCell,
	TableContainer,
	TableHeader,
	TableHeaderCell,
	TableRow,
} from './Table.styles';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { Button } from '../button';
import { ArrowDownward, ArrowUpward, FileDownload, PictureAsPdf } from '@mui/icons-material';
import { getImageUrl } from '../../utils';
import { tenant } from '../../config/tenant';

interface TableColumn {
	id: string;
	label: string;
	width?: string;
}

interface CellValue {
	value: ReactNode;
	rawValue?: string | number;
}

interface TableProps {
	columns: TableColumn[];
	rows?: Record<string, CellValue>[];
	exportToPDF?: boolean;
	exportToCSV?: boolean;
	onExportCSV?: () => void;
	title?: string;
	paginate?: boolean;
	pageSize?: number;
}

const Table: FC<TableProps> = ({
	columns,
	rows,
	exportToPDF = false,
	exportToCSV = false,
	onExportCSV,
	title = '',
	paginate = false,
	pageSize = 10,
}) => {
	const [exporting, setExporting] = useState(false);
	const [page, setPage] = useState(1);
	const [orderedRows, setOrderedRows] = useState<Record<string, CellValue>[]>();
	const [paginatedRows, setPaginatedRows] = useState<Record<string, CellValue>[]>();
	const [orderBy, setOrderBy] = useState('');
	const [asc, setAsc] = useState(true);
	const totalPages = useMemo(() => Math.ceil((rows?.length ?? 0) / pageSize), [rows, pageSize]);

	const handleExportToPDF = async () => {
		setExporting(true);
		const reportHeader = document.getElementById('reportHeader') as HTMLElement;
		const tableButtons = document.getElementById('tableButtons') as HTMLElement;
		reportHeader.style.display = 'block';
		tableButtons.style.display = 'none';
		const report = document.getElementById('table-report') as HTMLElement;

		const canvas = await html2canvas(report);

		const imgData = canvas.toDataURL('image/png');
		const pdf = new jsPDF('p', 'mm', 'a4');
		const imgWidth = 190;
		const imgHeight = (canvas.height * imgWidth) / canvas.width;

		pdf.addImage(imgData, 'PNG', 10, 10, imgWidth, imgHeight);
		pdf.save('reportCanalDoRepasse.pdf');
		setExporting(false);
		reportHeader.style.display = 'none';
		tableButtons.style.display = 'block';
	};

	const handleExportToCSV = () => {
		setExporting(true);
		if (!rows) {
			return;
		}

		const csvContent =
			'data:text/csv;charset=utf-8,' +
			columns.map((column) => column.label).join(',') +
			'\n' +
			rows
				.map((row) =>
					columns.map((column) => (row[column.id] ? row[column.id]?.toString() : '')).join(',')
				)
				.join('\n');

		const encodedUri = encodeURI(csvContent);
		const link = document.createElement('a');
		link.setAttribute('href', encodedUri);
		link.setAttribute('download', 'table.csv');
		document.body.appendChild(link);
		link.click();
		setExporting(false);
	};

	const handleSort = (columnId: string) => {
		if (!rows) {
			return;
		}

		let updatedAsc = asc;

		if (orderBy === columnId) {
			updatedAsc = !asc;
			setAsc(!asc);
		} else {
			updatedAsc = true;
			setAsc(true);
		}

		setOrderBy(columnId);

		const ordered = [...rows].sort((a, b) => {
			const valueA = a[columnId].rawValue || '';
			const valueB = b[columnId].rawValue || '';

			if (updatedAsc) {
				if (typeof valueA === 'number' && typeof valueB === 'number') {
					return valueA - valueB;
				}

				return valueA.toString().localeCompare(valueB.toString());
			}

			if (typeof valueA === 'number' && typeof valueB === 'number') {
				return valueB - valueA;
			}

			return valueB.toString().localeCompare(valueA.toString());
		});

		setOrderedRows(ordered);
	};

	useEffect(() => {
		setOrderedRows(rows);
	}, [rows]);

	useEffect(() => {
		setPage(1);
	}, [orderedRows]);

	useEffect(() => {
		if (paginate) {
			const start = (page - 1) * pageSize;
			const end = start + pageSize;
			setPaginatedRows(orderedRows?.slice(start, end));

			return;
		}

		setPaginatedRows(orderedRows);
	}, [paginate, page, pageSize, orderedRows]);

	return (
		<div id='table-report'>
			{(exportToPDF || exportToCSV) && (
				<ExportButtons id='tableButtons'>
					{exportToPDF && (
						<Button variant='text' onClick={handleExportToPDF} disabled={exporting}>
							<PictureAsPdf fontSize='inherit' />
							PDF
						</Button>
					)}
					{exportToCSV && (
				<Button variant='text' onClick={onExportCSV ?? handleExportToCSV} disabled={exporting}>
						<FileDownload fontSize='inherit' />
						CSV
					</Button>
					)}
				</ExportButtons>
			)}
			<ReportHeaderContainer id='reportHeader'>
				<img src={getImageUrl.logoPadrao()} alt={tenant.appName} width={200} />
				<h1>{title}</h1>
			</ReportHeaderContainer>
			<TableContainer>
				<TableHeader>
					<TableRow>
						{columns.map((column) => (
							<TableHeaderCell
								key={column.id}
								width={column.width}
								onClick={() => handleSort(column.id)}
							>
								<HeaderContainer>
									{orderBy === column.id && asc && <ArrowUpward />}
									{orderBy === column.id && !asc && <ArrowDownward />}
									{column.label}
								</HeaderContainer>
							</TableHeaderCell>
						))}
					</TableRow>
				</TableHeader>
				<TableBody>
					{paginatedRows?.map((row, index) => (
						<TableRow key={index}>
							{columns.map((column) => (
								<TableCell key={index + column.id}>{row[column.id].value}</TableCell>
							))}
						</TableRow>
					))}
				</TableBody>
			</TableContainer>
			{paginate && totalPages > 1 && (
				<PaginationContainer>
					<Button
						variant='pagination'
						page='prev'
						disabled={page === 1}
						onClick={() => setPage(Math.max(page - 3, 1))}
					/>
					{[...Array(Math.min(3, totalPages))].map((_, i) => {
						let displayPage = 0;
						if (totalPages <= 3 || page === 1) {
							displayPage = i + 1;
						} else if (page === totalPages) {
							displayPage = totalPages - 3 + i + 1;
						} else {
							displayPage = page - 1 + i;
						}

						return (
							<Button
								key={displayPage}
								variant='pagination'
								page={displayPage}
								active={displayPage === page}
								onClick={() => setPage(displayPage)}
							/>
						);
					})}
					<Button
						variant='pagination'
						page='next'
						disabled={page === totalPages}
						onClick={() => setPage(Math.min(page + 3, totalPages))}
					/>
				</PaginationContainer>
			)}
		</div>
	);
};

export default Table;
